import { FunctionalComponent } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';

import styles from './styles.module.scss';

interface NewsItemFieldData {
  name: string;
  slug: string;
  'main-image'?: { url: string; alt?: string };
  'date-and-time'?: string;
}

interface NewsItem {
  id: string;
  lastPublished: string;
  fieldData: NewsItemFieldData;
}

const getRelativeTime = (dateStr: string): string => {
  const diffDays = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  if (diffDays < 1) return 'today';
  if (diffDays === 1) return '1 day ago';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 14) return '1 week ago';
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 60) return '1 month ago';
  return `${Math.floor(diffDays / 30)} months ago`;
};

const NewsNotification: FunctionalComponent = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [news, setNews] = useState<NewsItem[]>([]);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetch(`${process.env.APP_URL}/api/webflow-news`)
      .then((res) => res.json())
      .then((data) => {
        const items: NewsItem[] = Array.isArray(data) ? data : data?.items || [];
        setNews(items.slice(0, 3));
      })
      .catch((err) => console.error('NewsNotification', err));
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handleOutsideClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isOpen]);

  if (!news.length) return null;

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <button
        className={`${styles.bell} ${isOpen ? styles.bellActive : ''}`}
        onClick={() => setIsOpen((v) => !v)}
        aria-label="News and updates"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6V11c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" fill="currentColor" />
        </svg>
      </button>

      {isOpen && (
        <div className={styles.dropdown}>
          {news.map((item) => {
            const { fieldData } = item;
            const thumbnail = fieldData['main-image']?.url;
            const date = fieldData['date-and-time'] || item.lastPublished;
            const href = `${process.env.APP_URL}/articles/${fieldData.slug}`;
            return (
              <a
                key={item.id}
                href={href}
                target="_blank"
                rel="noreferrer"
                className={styles.item}
                onClick={() => setIsOpen(false)}
              >
                {thumbnail && (
                  <img src={thumbnail} alt="" className={styles.thumbnail} />
                )}
                <div className={styles.text}>
                  <span className={styles.title}>{fieldData.name}</span>
                  {date && <span className={styles.time}>{getRelativeTime(date)}</span>}
                </div>
              </a>
            );
          })}
          <a
            href={`${process.env.APP_URL}/news-and-updates`}
            target="_blank"
            rel="noreferrer"
            className={styles.more}
            onClick={() => setIsOpen(false)}
          >
            More news
          </a>
        </div>
      )}
    </div>
  );
};

export default NewsNotification;
